import { InfiniteData, QueryClient } from '@tanstack/react-query'
import {
  TeamListEventsResponse,
  TeamOverviewEventsResponse,
} from 'app/frontend/base/api/event'

type TeamEventReferenceCacheSnapshot = {
  overview: [
    readonly unknown[],
    InfiniteData<TeamOverviewEventsResponse> | undefined,
  ][]
  calendar: [readonly unknown[], wowaudit.EventReference[] | undefined][]
  list: [readonly unknown[], TeamListEventsResponse | undefined][]
}

const updateEventReferences = (
  existing: wowaudit.EventReference[] | undefined,
  updatedEvent: Pick<wowaudit.EventReference, 'id'> &
    Partial<wowaudit.EventReference>,
) => {
  if (!existing) return existing
  return existing.map((eventReference) =>
    eventReference.id === updatedEvent.id
      ? { ...eventReference, ...updatedEvent }
      : eventReference,
  )
}

export const captureTeamEventReferenceCaches = (
  queryClient: QueryClient,
  teamId: number,
): TeamEventReferenceCacheSnapshot => {
  return {
    overview: queryClient.getQueriesData<
      InfiniteData<TeamOverviewEventsResponse>
    >({
      queryKey: ['teamEvents', { id: teamId, type: 'overview' }],
    }),
    calendar: queryClient.getQueriesData<wowaudit.EventReference[]>({
      queryKey: ['teamEvents', { id: teamId, type: 'calendar' }],
    }),
    list: queryClient.getQueriesData<TeamListEventsResponse>({
      queryKey: ['teamEvents', { id: teamId, type: 'list' }],
    }),
  }
}

export const restoreTeamEventReferenceCaches = (
  queryClient: QueryClient,
  snapshot: TeamEventReferenceCacheSnapshot,
) => {
  snapshot.overview.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data)
  })
  snapshot.calendar.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data)
  })
  snapshot.list.forEach(([queryKey, data]) => {
    queryClient.setQueryData(queryKey, data)
  })
}

export const patchTeamEventReferenceCaches = (
  queryClient: QueryClient,
  teamId: number,
  updatedEvent: Pick<wowaudit.EventReference, 'id'> &
    Partial<wowaudit.EventReference>,
) => {
  queryClient.setQueriesData<InfiniteData<TeamOverviewEventsResponse>>(
    {
      queryKey: ['teamEvents', { id: teamId, type: 'overview' }],
    },
    (existingData) => {
      if (!existingData) return existingData
      return {
        ...existingData,
        pages: existingData.pages.map((page) => ({
          ...page,
          events: updateEventReferences(page.events, updatedEvent) ?? [],
        })),
      }
    },
  )

  queryClient.setQueriesData<wowaudit.EventReference[]>(
    {
      queryKey: ['teamEvents', { id: teamId, type: 'calendar' }],
    },
    (existingData) => updateEventReferences(existingData, updatedEvent),
  )

  queryClient.setQueriesData<TeamListEventsResponse>(
    {
      queryKey: ['teamEvents', { id: teamId, type: 'list' }],
    },
    (existingData) => {
      if (!existingData) return existingData
      return {
        ...existingData,
        events: updateEventReferences(existingData.events, updatedEvent) ?? [],
      }
    },
  )
}
